import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Modal from '../../components/ui/Modal';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import PermissionMatrix from '../../components/permissions/PermissionMatrix';
import { createRole, updateRole, getRole, fetchPermissionCatalog } from '../../api/roles';
import { pushToast } from '../../components/ui/toastBus';

export default function RoleFormModal({ open, onClose, roleId }) {
  const queryClient = useQueryClient();
  const isEdit = !!roleId;

  const [name, setName] = useState('');
  const [permissions, setPermissions] = useState([]);
  const [errors, setErrors] = useState({});

  const catalogQuery = useQuery({ queryKey: ['permissions-catalog'], queryFn: fetchPermissionCatalog, enabled: open });
  const roleQuery = useQuery({
    queryKey: ['roles', roleId],
    queryFn: () => getRole(roleId),
    enabled: open && isEdit,
  });

  useEffect(() => {
    if (roleQuery.data) {
      setName(roleQuery.data.name);
      setPermissions(roleQuery.data.permissions);
    } else if (!isEdit) {
      setName('');
      setPermissions([]);
    }
  }, [roleQuery.data, isEdit, open]);

  const isSystem = roleQuery.data?.is_system;

  const mutation = useMutation({
    mutationFn: () => {
      const payload = isSystem ? { permissions } : { name, permissions };
      return isEdit ? updateRole(roleId, payload) : createRole({ name, permissions });
    },
    onSuccess: () => {
      pushToast({ type: 'success', message: isEdit ? 'Role updated.' : 'Role created.' });
      queryClient.invalidateQueries({ queryKey: ['roles'] });
      handleClose();
    },
    onError: (err) => setErrors(err.response?.data?.errors || {}),
  });

  const handleClose = () => {
    setErrors({});
    onClose();
  };

  const loading = catalogQuery.isLoading || (isEdit && roleQuery.isLoading);

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={isEdit ? 'Edit role' : 'Create role'}
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button onClick={() => mutation.mutate()} loading={mutation.isPending} disabled={loading}>
            {isEdit ? 'Save changes' : 'Create role'}
          </Button>
        </>
      }
    >
      {loading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : (
        <div className="space-y-4">
          <Input
            label="Role name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            error={errors.name?.[0]}
            disabled={isSystem}
          />

          {isSystem && (
            <p className="rounded-md bg-primary-50 px-3 py-2 text-sm text-primary-dark">
              super-admin has all permissions and cannot be edited.
            </p>
          )}

          <PermissionMatrix
            mode="role"
            groups={catalogQuery.data || {}}
            value={isSystem ? Object.values(catalogQuery.data || {}).flat().map((p) => p.name) : permissions}
            onChange={setPermissions}
            readOnly={isSystem}
          />
        </div>
      )}
    </Modal>
  );
}
